import { useState, useEffect } from 'react';
import { usersApi } from '../services/api';
import { User, Role, getRoleName } from '../types/models';
import { format } from 'date-fns';
import { Users, UserPlus, Mail, Phone, Calendar } from 'lucide-react';

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterRole, setFilterRole] = useState<number | null>(null);

  useEffect(() => {
    loadUsers();
  }, [filterRole]);

  const loadUsers = async () => {
    try {
      const data = await usersApi.getUsers(
        filterRole ? { role_id: filterRole } : undefined
      ) as User[];
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const groupedUsers = {
    [Role.ADMIN]: users.filter(u => u.role_id === Role.ADMIN),
    [Role.JEFE_TALLER]: users.filter(u => u.role_id === Role.JEFE_TALLER),
    [Role.RECEPCIONISTA]: users.filter(u => u.role_id === Role.RECEPCIONISTA),
    [Role.OPERARIO]: users.filter(u => u.role_id === Role.OPERARIO),
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users className="h-6 w-6" />
            Gestión de Usuarios
          </h1>
          <p className="text-gray-500">{users.length} usuarios registrados</p>
        </div>

        <div className="flex items-center gap-4">
          <select
            value={filterRole || ''}
            onChange={(e) => setFilterRole(e.target.value ? Number(e.target.value) : null)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Todos los roles</option>
            <option value={Role.ADMIN}>Administrador</option>
            <option value={Role.JEFE_TALLER}>Jefe de Taller</option>
            <option value={Role.RECEPCIONISTA}>Recepcionista</option>
            <option value={Role.OPERARIO}>Operario</option>
          </select>

          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Nuevo Usuario
          </button>
        </div>
      </div>

      {/* User Groups */}
      {Object.entries(groupedUsers).map(([role, roleUsers]) => {
        if (roleUsers.length === 0) return null;

        return (
          <div key={role} className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-3">
              {getRoleName(Number(role))} ({roleUsers.length})
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roleUsers.map((user) => (
                <div key={user.id} className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-xl font-bold text-blue-600">
                          {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{user.employee_number}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded ${
                      user.status_id === 10 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {user.status_id === 10 ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-500">
                      <Mail className="h-4 w-4" />
                      <span>{user.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-500">
                      <Phone className="h-4 w-4" />
                      <span>{user.phone || 'Sin teléfono'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-500">
                      <Calendar className="h-4 w-4" />
                      <span>Ingreso: {format(new Date(user.hire_date), 'dd MMM yyyy')}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                    <button className="flex-1 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded transition">
                      Editar
                    </button>
                    <button className="flex-1 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded transition">
                      Ver horarios
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
